import { Box, Group } from '@mantine/core';
import type { ISingleSelectorProps } from '@repo/common/components/SingleSelector/SingleSelector';
import SingleSelector from '@repo/common/components/SingleSelector/SingleSelector';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { Button, Icon, Text, type IconNames } from '@repo/foundations';
import { useMemo } from 'react';
import { useTagList } from '../../../api';

interface TagSelectorProps
	extends Pick<ISingleSelectorProps, 'readOnly' | 'target' | 'withinPortal'> {
	onChange: (id: string) => void;
	initialValue?: string;
	allowEmptyOption?: boolean;
}

export function TagSelector({
	onChange,
	initialValue,
	allowEmptyOption = false,
	...singleSelectorProps
}: TagSelectorProps) {
	const { data: tags } = useTagList({
		filters: { visible: true },
	});

	const tagOptions = useMemo<SelectablePropertyItem[]>(
		() => [
			...(allowEmptyOption
				? [
						{
							label: 'All tags',
							value: '',
							iconName: 'tag' as IconNames,
						},
					]
				: []),
			...(tags?.results.map((tag) => ({
				value: tag.id,
				label: tag.name,
				icon: (
					<Icon
						name="circle"
						style={{ fill: tag.color, color: tag.color, width: 12 }}
					/>
				),
			})) ?? []),
		],
		[tags?.results, allowEmptyOption]
	);

	const selectedTag = useMemo(
		() => tagOptions.find((opt) => opt.value === initialValue),
		[initialValue, tagOptions]
	);

	return (
		<Box maw={512}>
			<SingleSelector
				variant="default"
				iconType="emoji"
				isViewerUser={false}
				searchable
				options={tagOptions}
				placeholder="All tags"
				placeholderIconName="tag"
				initialSelected={initialValue}
				onChange={(id) => onChange(id)}
				property="tag"
				target={
					<Button size="md">
						<Group spacing="3xs" noWrap>
							{selectedTag?.icon ? selectedTag.icon : <Icon name="tag" />}
							<Text size="sm">{selectedTag?.label || 'All tags'}</Text>
						</Group>
					</Button>
				}
				{...singleSelectorProps}
			/>
		</Box>
	);
}
